import { computed, ref } from 'vue';
import dayjs from 'dayjs';

export type BookingMode = 'slot' | 'date' | 'store';

/** Name of App\Filters\Equipment\AvailabilityFilter, as exposed to the equipment filter bar. */
export const AVAILABILITY_FILTER = 'available_between';

export interface UseBookingDraftOptions {
  mode: BookingMode;
  /** Filters the equipment list always carries, before the store narrowing. */
  equipmentBaseFilters?: Record<string, any>;
  stores?: any[];
  /** Existing booking when editing; omitted when creating. */
  booking?: Record<string, any> | null;
}

/**
 * Holds the booking being built, for both the create and the edit screens.
 *
 * Dates follow a pending/commit model: the date dialog reports every interaction, but only an
 * explicit commit writes to the submitted values. Without it, closing the dialog without saving
 * would still change what gets posted.
 */
export function useBookingDraft(options: UseBookingDraftOptions) {
  const { mode, equipmentBaseFilters = {}, stores = [], booking: initial = null } = options;

  const booking = ref<Record<string, any>>({
    id: initial?.id ?? null,
    store: initial?.store ?? null,
    equipment: initial?.equipment ?? null,
    registration: initial?.registration ?? null,
    booking_slot: initial?.booking_slot ?? null,
    start_at: initial?.start_at ?? null,
    end_at: initial?.end_at ?? null,
  });

  const selectedEquipment = ref<number | null>(initial?.equipment?.id ?? null);
  const selectedStore = ref<number | null>(initial?.store?.id ?? null);
  const selectedSlot = ref<number | null>(initial?.booking_slot?.id ?? null);
  const selectedRegistration = ref<number | null>(initial?.registration?.id ?? null);
  const selectedDateRange = ref<{ start: string | null; end: string | null }>({
    start: initial?.start_at ? dayjs(initial.start_at).toISOString() : null,
    end: initial?.end_at ? dayjs(initial.end_at).toISOString() : null,
  });

  /** Date dialog edits land here first and only reach the draft on commitDates(). */
  const pendingDateRange = ref<{ start: string | null; end: string | null } | null>(null);
  const pendingSlot = ref<any | null>(null);

  const bookableId = computed<number | null>(() => {
    if (mode === 'slot') {
      return selectedSlot.value;
    }
    if (mode === 'store') {
      return selectedStore.value;
    }

    return null;
  });

  /** Set once the user needs to re-check their equipment against a period that moved under it. */
  const equipmentNeedsRecheck = ref(false);

  /**
   * The committed period the equipment must be free for. In slot mode it comes from the slot
   * itself; store mode is excluded, its equipment is picked before any date exists.
   */
  const availabilityWindow = computed<{ start: string; end: string } | null>(() => {
    if (mode === 'store') {
      return null;
    }

    const [start, end] = mode === 'slot'
      ? [booking.value.booking_slot?.start_at, booking.value.booking_slot?.end_at]
      : [selectedDateRange.value.start, selectedDateRange.value.end];

    return start && end
      ? { start: dayjs(start).toISOString(), end: dayjs(end).toISOString() }
      : null;
  });

  const equipmentFilters = computed(() => (
    booking.value.store
      ? { ...equipmentBaseFilters, store_id: booking.value.store.id }
      : equipmentBaseFilters
  ));

  /**
   * Pre-fills the availability filter with the chosen period, so the equipment list opens already
   * restricted to it. It stays an ordinary filter: the user can widen, narrow or clear it from the
   * filter bar, clearing being how you get back to the whole catalogue.
   */
  function withAvailabilityDefault(definitions: any[]): any[] {
    const window = availabilityWindow.value;

    if (!window) {
      return definitions;
    }

    return definitions.map((definition) => (
      definition?.name === AVAILABILITY_FILTER
        ? { ...definition, default: `${window.start}:${window.end}` }
        : definition
    ));
  }

  /** Whether the date step is satisfied, which gates the registration step. */
  const hasDates = computed<boolean>(() => {
    if (mode === 'slot') {
      return Boolean(booking.value.booking_slot);
    }
    if (mode === 'store') {
      return Boolean(booking.value.start_at && booking.value.end_at && selectedStore.value);
    }

    return Boolean(booking.value.start_at && booking.value.end_at);
  });

  function setStore(storeId: number | string): void {
    const store = stores.find((candidate) => Number(candidate.id) === Number(storeId));

    booking.value.store = store ?? null;
    selectedStore.value = store?.id ?? null;

    // The equipment list is scoped by store, so a previous pick may no longer be valid.
    booking.value.equipment = null;
    selectedEquipment.value = null;
    booking.value.start_at = null;
    booking.value.end_at = null;
  }

  function setEquipment(equipment: any): void {
    booking.value.equipment = equipment;
    selectedEquipment.value = equipment?.id ?? null;
    equipmentNeedsRecheck.value = false;
  }

  function setRegistration(registration: any): void {
    booking.value.registration = registration;
    selectedRegistration.value = registration?.id ?? null;
  }

  function stageDateRange(range: { start: any; end: any }): void {
    pendingDateRange.value = {
      start: range?.start ? dayjs(range.start).toISOString() : null,
      end: range?.end ? dayjs(range.end).toISOString() : null,
    };
  }

  /** StoreDateSelector reports its own shape. */
  function stageStoreSlot(storeSlot: { start_at: any; end_at: any }): void {
    stageDateRange({ start: storeSlot?.start_at, end: storeSlot?.end_at });
  }

  function stageSlot(bookingSlot: any): void {
    pendingSlot.value = bookingSlot;
  }

  function commitDates(): void {
    const previousWindow = availabilityWindow.value;

    if (pendingSlot.value) {
      booking.value.booking_slot = pendingSlot.value;
      selectedSlot.value = pendingSlot.value.id ?? null;
      pendingSlot.value = null;
    }

    if (pendingDateRange.value) {
      selectedDateRange.value = pendingDateRange.value;
      booking.value.start_at = pendingDateRange.value.start;
      booking.value.end_at = pendingDateRange.value.end;
      pendingDateRange.value = null;
    }

    // The equipment was picked as free over the old period; the new one may not be.
    const movedWindow = previousWindow?.start !== availabilityWindow.value?.start
      || previousWindow?.end !== availabilityWindow.value?.end;

    if (movedWindow && previousWindow && booking.value.equipment) {
      equipmentNeedsRecheck.value = true;
    }
  }

  function discardPendingDates(): void {
    pendingDateRange.value = null;
    pendingSlot.value = null;
  }

  return {
    booking,
    selectedEquipment,
    selectedStore,
    selectedSlot,
    selectedRegistration,
    selectedDateRange,
    bookableId,
    equipmentFilters,
    hasDates,
    availabilityWindow,
    equipmentNeedsRecheck,
    withAvailabilityDefault,
    setStore,
    setEquipment,
    setRegistration,
    stageDateRange,
    stageStoreSlot,
    stageSlot,
    commitDates,
    discardPendingDates,
  };
}
